import { Injectable } from '@angular/core';
import {
  HttpResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoaderService } from './loader.service';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {

  private requests: HttpRequest<any>[] = [];

  constructor(private loaderService: LoaderService) { }

  private removeRequest(req: HttpRequest<any>) {
    const i = this.requests.indexOf(req);
    if (i >= 0) {
      this.requests.splice(i, 1);
    }

    if (this.requests.length === 0) {
      this.loaderService.hide();
    } else {
      // Update message for remaining requests
      this.loaderService.updateMessage(`Processing ${this.requests.length} request${this.requests.length > 1 ? 's' : ''}`);
    }
  }

  private getLoadingMessage(req: HttpRequest<any>): string {
    const url = (req.url || '').toLowerCase();
    const method = (req.method || 'GET').toUpperCase();
    // Context-aware messages for candidate assessment question navigation
    if (url.includes('candidate/assessments')) {
      // Save-and-fetch next question or explicit question fetch
      if (url.includes('/save') || url.includes('get-question-by-index') || url.endsWith('/view')) {
        return 'Loading question';
      }
    }
    // Context-aware messages for help articles
    if (url.includes('help.evaltrix.com/api/articles')) {
      return 'Loading help articles';
    }
    // Context-aware messages for common auth flows
    if (method === 'POST') {
      if (url.includes('forgot-password')) {
        return 'Sending reset link';
      }
      if (url.includes('reset-password')) {
        return 'Setting password';
      }
      if (url.includes('verify-email')) {
        return 'Verifying email';
      }
      if (url.includes('resend-verify-link') || url.includes('resend-verify')) {
        return 'Resending verification email';
      }
      if (url.includes('signup')) {
        return 'Creating account';
      }
      if (url.includes('login')) {
        return 'Signing in';
      }
    }
    // Context-aware messages for employer/enterprise dashboard
    if (url.includes('/enterprise/')) {
      // Assessments
      if (url.includes('/assessments/')) {
        // Downloads
        if (url.includes('/download-answers')) {
          return 'Downloading answers';
        }
        if (url.includes('/download-question-paper')) {
          return 'Downloading paper';
        }
        // Candidates
        if (url.includes('/candidates')) {
          if (url.includes('download=true')) {
            return 'Downloading candidates';
          }
          return 'Loading candidates';
        }
        if (url.endsWith('/list')) {
          return 'Loading assessments';
        }
        if (url.includes('/clone')) {
          return 'Cloning assessment';
        }
        if (url.includes('/question-type-info')) {
          return 'Loading settings';
        }
        if (url.includes('/questions')) {
          return 'Loading questions';
        }
        if (method === 'POST' && url.includes('/create')) {
          return 'Creating assessment';
        }
        if (method === 'PUT' && (url.includes('/soft-update') || url.match(/\/assessments\/[^/]+$/))) {
          return 'Saving assessment';
        }
        if (method === 'GET' && url.match(/\/assessments\/[^/]+$/)) {
          return 'Loading details';
        }
        if (url.includes('/get-invite-link')) {
          return 'Generating invite link';
        }
      }
      // Questions
      if (url.includes('/questions/')) {
        if (method === 'POST' && url.includes('/create')) {
          return 'Creating question';
        }
        if (method === 'PUT') {
          return 'Updating question';
        }
        if (method === 'DELETE') {
          return 'Deleting question';
        }
        if (url.includes('/run-tests')) {
          return 'Running tests';
        }
        if (url.includes('/list')) {
          return 'Loading questions';
        }
      }
      // Templates
      if (url.includes('/templates')) {
        if (url.endsWith('/list')) {
          return 'Loading templates';
        }
        if (method === 'POST' && url.includes('/create')) {
          return 'Creating template';
        }
        if (method === 'PUT') {
          return 'Updating template';
        }
        if (method === 'DELETE') {
          return 'Deleting template';
        }
      }
      // Profiles and company
      if (url.includes('/admin-profile')) {
        return method === 'GET' ? 'Loading profile' : 'Updating profile';
      }
      if (url.includes('/company-profile')) {
        return method === 'GET' ? 'Loading company profile' : 'Updating company profile';
      }
      if (url.includes('/get-logo')) {
        return 'Loading logo';
      }
      if (url.includes('validate-admin-token')) {
        return 'Verifying invitation';
      }
      if (url.includes('/invite-admins')) {
        return 'Inviting team member';
      }
      if (url.includes('/uninvite-admins')) {
        return 'Removing team member';
      }
      // Notifications
      if (url.includes('/notifications')) {
        return 'Loading notifications';
      }
      if (url.includes('/notification-count')) {
        return 'Loading notifications';
      }
      // Topics/subtopics
      if (url.includes('/topic/enterprise-only') || url.includes('/topic/used-in-questions')) {
        return 'Loading topics';
      }
      if (url.includes('/topic/new-topic')) {
        return method === 'POST' ? 'Creating topic' : 'Loading topics';
      }
      if (url.match(/\/topic\/[^/]+$/) && method === 'DELETE') {
        return 'Deleting topic';
      }
      if (url.includes('/sub-topic') && method === 'POST') {
        return 'Creating subtopic';
      }
      if (url.includes('/sub-topics/list')) {
        return 'Loading subtopics';
      }
      if (url.includes('/coding-subtopic') && method === 'POST') {
        return 'Creating coding subtopic';
      }
      if (url.includes('/coding-subtopics/list')) {
        return 'Loading coding subtopics';
      }
      // Prompt hints
      if (url.includes('/prompt-hints')) {
        return 'Loading prompt hints';
      }
      // Subscriptions/billing
      if (url.includes('/subscriptions/')) {
        if (url.includes('/invoices')) {
          return 'Loading invoices';
        }
        if (url.includes('/cancel')) {
          return 'Cancelling subscription';
        }
      }
      // Default enterprise fallback by method
      switch (method) {
        case 'POST':
          return 'Submitting changes';
        case 'PUT':
          return 'Saving changes';
        case 'DELETE':
          return 'Deleting item';
        case 'GET':
          return 'Loading data';
      }
    }
    switch (method) {
      case 'POST':
        return 'Saving data';
      case 'PUT':
        return 'Updating data';
      case 'DELETE':
        return 'Deleting data';
      case 'GET':
        return 'Loading data';
      default:
        return 'Processing...';
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip loader for specific requests
    if (req.headers.has('skip-loader')) {
      return next.handle(req);
    }

    this.requests.push(req);
    const message = this.getLoadingMessage(req);
    this.loaderService.show(message);

    return next.handle(req).pipe(
      finalize(() => {
        this.removeRequest(req);
      })
    );
  }
}
